import { useCallback, useState } from 'react';
import { type AssignableRole, Roles } from '@/utils/roleUtils';
import { computeExpiryDate } from '@/components/common/access-time-selector/utils';
import type { AccessGrantFlow } from '@/hooks/sharing/accessGrantFlow';

export type ShareFormData = {
	email: string;
	name?: string;
	purpose?: string;
	company?: string;
	role?: AssignableRole;
	/** The code of the role a new contact card is filed under. */
	contactRole?: string;
	/** Personal note included in the invite email — distinct from access_purpose, which is the
	 * guest-request-reason field and shouldn't carry an inviter-authored message. */
	message?: string;
};

export type SentInvitationData = {
	email: string;
	expiresAt: Date | null;
};

/**
 * The invite dialog's form state: the duration, what was just sent, and the running list of it.
 *
 * What submitting means is the flow's, not this hook's — the Access page adds and invites, the
 * contacts page also files a contact. Everything here is about the form, so both get it.
 */
export const useShareForm = (flow: AccessGrantFlow) => {
	const [expiresAt, setExpiresAt] = useState<Date | null>(() => computeExpiryDate('today'));
	const [success, setSuccess] = useState(false);
	const [sentInvitation, setSentInvitation] = useState<SentInvitationData | null>(null);
	const [recentInvites, setRecentInvites] = useState<SentInvitationData[]>([]);

	const submitInvitation = async (formData: ShareFormData) => {
		const ok = await flow.submit({
			email: formData.email.trim(),
			name: formData.name,
			purpose: formData.purpose,
			message: formData.message,
			role: formData.role ?? Roles.Viewer,
			contactRole: formData.contactRole,
			expiresAt,
		});
		if (!ok) return false;

		const sent: SentInvitationData = { email: formData.email.trim(), expiresAt };
		setSentInvitation(sent);
		setRecentInvites((prev) => [sent, ...prev].slice(0, 10));
		setSuccess(true);
		return true;
	};

	// Stable identity: every call sets a fresh array and Date, so any caller that depends on this
	// in an effect would re-run itself forever if the function changed each render.
	const resetForm = useCallback(() => {
		setSuccess(false);
		setSentInvitation(null);
		setRecentInvites([]);
		// Reset the duration back to the default so the picker's pill doesn't persist the last
		// selection across open/close (the picker derives its pill from expiresAt on mount).
		setExpiresAt(computeExpiryDate('today'));
	}, []);

	return {
		expiresAt,
		setExpiresAt,
		success,
		sentInvitation,
		recentInvites,
		isLoading: flow.isPending,
		submitInvitation,
		resetForm,
	};
};
